import { object, string } from "yup";
import { z } from "zod";
import type { VendorCompany } from "~/models/VendorCompany";
import {
	applyPasswordRules,
	createPasswordZodSchema,
	refineDifferentFromOldPassword
} from "~/utils/PasswordValidationUtil";

const SLACK_MEMBER_ID_REGEX = /^[UW][A-Z0-9]{8,}$/;

export type VendorUser = {
	email: string;
	enabled: boolean;
	hasPassword: boolean;
	id: number;
	name: string;
	seq: number;
	slackMemberId: string | null;
	tel: string;
};

export type VendorUserWithCompany = VendorUser & {
	vendorCompany: VendorCompany;
};

export type VendorUserSelfUpdateParam = {
	email: string;
	name: string;
	slackMemberId?: string | null;
	tel: string;
};

export type VendorUserSettingParam = VendorUserSelfUpdateParam;

export type VendorUserCreateParam = VendorUserSettingParam & {
	password: string;
};

export type VendorUserFormValue = VendorUserSettingParam & {
	confirmPassword: string;
	password: string;
};

export type VendorUserUpdatePasswordParam = {
	confirmPassword: string;
	newPassword: string;
	oldPassword: string;
};

export type VendorUserPasswordResetParams = {
	confirmPassword: string;
	newPassword: string;
};

const slackMemberIdValidation = string()
	.nullable()
	.notRequired()
	.test(
		"slack-member-id-format",
		"Slack IDはUまたはWから始まる英数字で入力してください",
		(value) => {
			if (value === undefined || value === null || value === "") {
				return true;
			}

			return SLACK_MEMBER_ID_REGEX.test(value);
		}
	);

export const createVendorUserValidationScheme = (
	canEditSlackMemberId: boolean,
	isNew = false
) =>
	object().shape({
		...(isNew
			? {
					confirmPassword: string()
						.required("パスワード（確認用）を入力してください")
						.test(
							"passwordConfirm",
							"パスワードが一致しません",
							function testConfirmPassword(value: string | undefined) {
								const { password } = this.parent;
								if (!password) {
									return true;
								}
								return value === password;
							}
						),
					password: applyPasswordRules(
						string().required("パスワードを入力してください")
					)
				}
			: {}),
		email: string()
			.required("メールアドレスを入力してください")
			.email("メールアドレスの形式を確認してください"),
		name: string().required("氏名を入力してください"),
		...(canEditSlackMemberId ? { slackMemberId: slackMemberIdValidation } : {}),
		tel: string().required("電話番号を入力してください")
	});

export const vendorUserValidationScheme =
	createVendorUserValidationScheme(true);

const passwordSchema = createPasswordZodSchema(
	"新しいパスワードを入力してください"
);

const passwordFieldsShape = {
	confirmPassword: z
		.string()
		.min(1, "新しいパスワード（確認用）を入力してください"),
	newPassword: passwordSchema
};

const withPasswordConfirmationRefinement = <
	S extends z.ZodType<{ confirmPassword: string; newPassword: string }>
>(
	schema: S
) =>
	schema.superRefine((value, context) => {
		if (
			value.confirmPassword !== "" &&
			value.confirmPassword !== value.newPassword
		) {
			context.addIssue({
				code: "custom",
				message: "パスワードが一致しません",
				path: ["confirmPassword"]
			});
		}
	});

export const createVendorUserPasswordValidationSchema = (
	hasPassword: boolean
) =>
	withPasswordConfirmationRefinement(
		z.object({
			...passwordFieldsShape,
			oldPassword: hasPassword
				? z.string().min(1, "現在のパスワードを入力してください")
				: z.string()
		})
	).superRefine(refineDifferentFromOldPassword);

export const vendorUserPasswordResetValidationSchema =
	withPasswordConfirmationRefinement(z.object(passwordFieldsShape));

export const convertToComboBoxOption = (
	vendorUser: VendorUser
): { label: string; value: string } => ({
	label: vendorUser.name,
	value: String(vendorUser.id)
});
